import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeSlug from 'rehype-slug'
import { TypographyStylesProvider } from '@mantine/core'
import { Link } from 'react-router-dom'
import { useDocContent } from '@/hooks/useDocContent'
import { CodeBlockToolbar } from './CodeBlockToolbar'

interface HastNode {
  type?: string
  value?: string
  children?: HastNode[]
  properties?: { className?: string[] }
}

/** Recursively collect all text values from a hast node tree. */
function collectText(node: HastNode): string {
  if (node.value != null) return node.value
  return (node.children ?? []).map(collectText).join('')
}

function extractRawText(node: unknown): string {
  const n = node as HastNode
  // <pre> → <code> → [highlight spans | text]
  const codeChild = n?.children?.[0]
  if (!codeChild) return ''
  const raw = collectText(codeChild)
  // Remove trailing newline that rehype-highlight appends
  return raw.replace(/\n$/, '')
}

function extractLang(node: unknown): string {
  const n = node as HastNode
  const codeChild = n?.children?.[0]
  const cls = codeChild?.properties?.className?.find((c: string) => c.startsWith('language-'))
  return cls ? cls.replace('language-', '') : 'text'
}

function isInternalLink(href: string | undefined): href is string {
  if (!href) return false
  // Internal: starts with "/", "?", or "#"
  return /^[/?#]/.test(href)
}

const components: Components = {
  pre({ node, children, ...rest }) {
    const raw = extractRawText(node).replace(/\n$/, '')
    const lang = extractLang(node)
    return (
      <CodeBlockToolbar code={raw} lang={lang}>
        <pre {...rest}>{children}</pre>
      </CodeBlockToolbar>
    )
  },
  a({ href, children, node: _node, ...rest }) {
    if (isInternalLink(href)) {
      // Use react-router Link to preserve sessionStorage (no full reload)
      return <Link to={href} {...rest}>{children}</Link>
    }
    // External: open in new tab with noopener
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    )
  },
}

interface Props {
  slug: string
}

export function MarkdownContent({ slug }: Props) {
  const content = useDocContent(slug)
  return (
    <TypographyStylesProvider>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug, rehypeHighlight]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </TypographyStylesProvider>
  )
}
